import {
    CoreModule,
    LoadStatus,
    MeasureEntity,
    MeasureEntityPickResult,
    PickSampleExt,
    SnapTolerance,
    View,
} from "@novorender/api";
import { ReadonlyVec2, vec2, vec3 } from "gl-matrix";
import {
    MouseEvent,
    MutableRefObject,
    PointerEvent as ReactPointerEvent,
    TouchEvent as ReactTouchEvent,
    useEffect,
    useRef,
    WheelEvent as ReactWheelEvent,
} from "react";

import { useAppDispatch, useAppSelector, useAppStore } from "app/redux-store-interactions";
import { store } from "app/store";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { highlightActions, useDispatchHighlighted, useLazyHighlighted } from "contexts/highlighted";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { PickingSnapType, usePickingSessionRef } from "contexts/pickingSession";
import { selectShowTracer } from "features/alignment/selectors";
import { useClippingPlaneActions } from "features/clippingPlanes/useClippingPlaneActions";
import { clearTempCoordinatesLabel, updateTempCoordinateLabel } from "features/coordinates";
import { selectDraggingCoordinate, selectHideCoordinateGizmo } from "features/coordinates/coordinatesSlice";
import { selectIsMarkerPointerInteractionActive } from "features/markers/markersSlice";
import {
    measureActions,
    selectMeasure,
    selectMeasureHoverSettings,
    selectObjectMeasureIds,
    useObjectMeasureHover,
} from "features/measure";
import { HOVER_CURSOR_PROGRESS_COLOR, HOVER_CURSOR_PROGRESS_ID } from "features/measure/utils";
import { myLocationActions, selectMyLocationAutocenter } from "features/myLocation";
import { orthoCamActions, selectCrossSectionPoint } from "features/orthoCam";
import {
    clippingOutlineLaserActions,
    selectDraggingLaser,
    selectHideGizmo,
    selectLockedRotation,
} from "features/outlineLaser";
import { getLaserGizmoAlignmentRotation, getLaserGizmoCoords } from "features/outlineLaser/utils";
import { pointLineActions, selectDraggingPoint, selectPointLineCurrentIdx, selectPointLines } from "features/pointLine";
import { useAddPointToPointLine } from "features/pointLine/hooks/useAddPointToPointLine";
import { pushMovePointUndoRedo } from "features/pointLine/pointLineUndoRedo";
import { getPointLineSnapInfo } from "features/pointLine/utils";
import { selectPointVisualizationStamp } from "features/pointVisualization/selectors";
import { undoRedoActions } from "features/undoRedo";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import { useIsMobileMeasureInteraction } from "hooks/useIsMobileMeasureInteraction";
import {
    selectIsProcoreDesign,
    selectJoysticksMoving,
    selectNewDesign,
    selectNodeClassSelectionEnabled,
    selectSelectionResolution,
} from "slices/explorer";
import { telemetry } from "telemetry";
import { type ExtendedMeasureEntity, ViewMode } from "types/misc";
import { IdArrayShrinker } from "utils/idArrayShrinker";
import { hasMouseSupport } from "utils/misc";
import { addLoadingHandle, removeLoadingHandle } from "utils/objectActions";
import { applySelectionDelta, pickKeptPrimary, resolveSelectionDelta } from "utils/resolveBulkSelection";
import { createLatestGate, queryView } from "utils/viewQueries";

import { getSnapAdjacentVertices, getSnapGuides2d, getSnapInfo, getSnapInfo2d } from "../measureSnap";
import { getPlaneCreationNormal } from "../planeCreationNormal";
import {
    renderActions,
    selectCamera,
    selectCameraDefaults,
    selectCameraType,
    selectClippingPlanes,
    selectCurrentPickerGeneratedParametricData,
    selectDefaultPointVisualization,
    selectIsXRayActive,
    selectMainObject,
    selectMainObjects,
    selectPickCameraPlane,
    selectPicker,
    selectSelectBox,
    selectStamp,
    selectSubtrees,
    selectViewMode,
} from "../renderSlice";
import { resolveSelectionTarget } from "../selectionResolution";
import { spacebarPanState } from "../spacebarPanState";
import { moveSvgCameraCursor, moveSvgCursor, moveSvgCursorDimension, moveSvgCursorPointSnap } from "../svgUtils";
import { CameraType, Picker, StampKind, SubtreeStatus } from "../types";
import { applyCameraDistanceToMeasureTolerance } from "../utils";
import { beginSelectBoxAbort, getSelectBoxAbortGeneration } from "./sharedSelectBoxAbort";
import { useCanvasContextMenuHandler } from "./useCanvasContextMenuHandler";

type PointerMoveEvent = Pick<ReactPointerEvent<HTMLCanvasElement>, "movementX" | "movementY" | "buttons"> & {
    nativeEvent: Pick<ReactPointerEvent<HTMLCanvasElement>["nativeEvent"], "offsetX" | "offsetY">;
    forceAsyncPick?: boolean;
    handleEvenWhenNoMovement?: boolean;
};

export function useCanvasEventHandlers({
    pointerPosRef,
    cursor,
    svg,
    engine2dRenderFnRef,
    pointerDownStateRef,
}: {
    pointerPosRef: MutableRefObject<[x: number, y: number]>;
    cursor: "measure" | "cross" | "pointSnap" | "standard";
    svg: SVGSVGElement | null;
    engine2dRenderFnRef: MutableRefObject<((moved: boolean, isIdleFrame: boolean) => void) | undefined>;
    pointerDownStateRef: MutableRefObject<
        | {
              timestamp: number;
              x: number;
              y: number;
          }
        | undefined
    >;
}) {
    const {
        state: { view, canvas, size, dataAccess },
    } = useExplorerGlobals();
    const { handleContextMenu: handleCanvasContextMenu } = useCanvasContextMenuHandler();
    const handleCanvasContextMenuRef = useRef(handleCanvasContextMenu);
    handleCanvasContextMenuRef.current = handleCanvasContextMenu;
    const measureHoverSettings = useAppSelector(selectMeasureHoverSettings);
    const pickingSessionRef = usePickingSessionRef();
    const measure = useAppSelector(selectMeasure);
    const clippingPlanes = useAppSelector(selectClippingPlanes);
    const picker = useAppSelector(selectPicker);
    const pickCameraPlane = useAppSelector(selectPickCameraPlane);
    const objectMeasureIds = useAppSelector(selectObjectMeasureIds);
    const objectMeasureHover = useObjectMeasureHover();
    const crossSectionPoint = useAppSelector(selectCrossSectionPoint);
    const stamp = useAppSelector(selectStamp);
    const subtrees = useAppSelector(selectSubtrees);
    const cameraType = useAppSelector(selectCameraType);
    const cameraState = useAppSelector(selectCamera);
    const roadLayerTracerEnabled = useAppSelector(selectShowTracer);
    const viewMode = useAppSelector(selectViewMode);
    const allowGeneratedParametric = useAppSelector(selectCurrentPickerGeneratedParametricData);
    const lastMeasurePickResult = useRef<PickSampleExt>();
    const downloadHoveredBrepTimer = useRef<ReturnType<typeof setTimeout>>();
    const dispatch = useAppDispatch();
    const dispatchLastPickSample = useDispatchLastPickSample();
    const { movePlanes } = useClippingPlaneActions();
    const movingPlaneControl = useRef<ReturnType<typeof movePlanes>>();
    const defaultPointVisualization = useAppSelector(selectDefaultPointVisualization);
    const pointVisStamp = useAppSelector(selectPointVisualizationStamp);
    const newDesign = useAppSelector(selectNewDesign);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isMobileMeasureInteraction = useIsMobileMeasureInteraction();
    const lockedLaserRotation = useAppSelector(selectLockedRotation);
    const isCameraCursorDrawn = useRef(false);
    const cameraDefaults = useAppSelector(selectCameraDefaults);
    const isDraggingLaser = Boolean(useAppSelector(selectDraggingLaser));
    const draggingPoint = useAppSelector(selectDraggingPoint);
    const isDraggingPoint = Boolean(draggingPoint);
    const isDraggingCoordinate = Boolean(useAppSelector(selectDraggingCoordinate));
    const isMarkerPointerInteractionActive = useAppSelector(selectIsMarkerPointerInteractionActive);
    const hideLaserGizmo = Boolean(useAppSelector(selectHideGizmo));
    const hideCoordinateGizmo = Boolean(useAppSelector(selectHideCoordinateGizmo));
    const mouseButtonPressed = useRef(false);
    const touchStartPos = useRef<ReadonlyVec2 | null>(null);
    const touchCount = useRef(0);
    const movedTouchAfterPressed = useRef(false);
    const lastSelectBoxCompletionTime = useRef<number>(0);
    const uom = useUom();
    const currentPointLineIdx = useAppSelector(selectPointLineCurrentIdx);
    const pointLines = useAppSelector(selectPointLines);
    const selectBox = useAppSelector(selectSelectBox);
    const isXRayActive = useAppSelector(selectIsXRayActive);
    const appStore = useAppStore();
    const lazyHighlightedObjects = useLazyHighlighted();
    const dispatchHighlighted = useDispatchHighlighted();
    const selectionResolution = useAppSelector(selectSelectionResolution);
    const nodeClassSelectionEnabled = useAppSelector(selectNodeClassSelectionEnabled);
    const joysticksMoving = useAppSelector(selectJoysticksMoving);

    const addPointToPointLine = useAddPointToPointLine();

    const hideHoverCursorProgress = () => {
        const hoverCursorProgress = document.querySelector(`#${HOVER_CURSOR_PROGRESS_ID}`) as HTMLDivElement;
        if (hoverCursorProgress) {
            hoverCursorProgress.style.display = "none";
        }
    };

    const showHoverCursorProgress = (x: number, y: number) => {
        const hoverCursorProgress = document.querySelector(`#${HOVER_CURSOR_PROGRESS_ID}`) as HTMLDivElement;
        if (hoverCursorProgress) {
            hoverCursorProgress.style.display = "block";
            hoverCursorProgress.style.left = `${x}px`;
            hoverCursorProgress.style.top = `${y}px`;
        }
    };

    const hideSvgCursor = () => {
        if (svg && view && moveSvgCursor) {
            moveSvgCursor({
                svg,
                view,
                size,
                pickResult: undefined,
                x: -100,
                y: -100,
                color: "",
                overrideKind: undefined,
            });
            if (picker === Picker.Coordinates) {
                clearTempCoordinatesLabel(svg);
            }
        }

        hideHoverCursorProgress();
    };

    const pickerRef = useRef(picker);
    if (picker !== pickerRef.current) {
        hideSvgCursor();
    }
    pickerRef.current = picker;

    const hideSvgCameraCursor = () => {
        if (svg && view && isCameraCursorDrawn.current) {
            moveSvgCameraCursor({ svg, view, x: -100, y: -100, canvas });
            isCameraCursorDrawn.current = false;
            if (canvas) {
                canvas.style.cursor = spacebarPanState.held ? "move" : "default";
            }
        }
    };

    const doMoveSvgCameraCursor = (x: number, y: number, buttons: number) => {
        if (svg && view && !joysticksMoving) {
            const isMouse = hasMouseSupport();
            const orbitOnPrimaryButton = cameraDefaults.pinhole.controller === "cadMiddlePan";
            const orthoMouseRotateEnabled =
                cameraState.type === CameraType.Orthographic
                    ? cameraDefaults.orthographic.mouseRotate &&
                      cameraState.fixOrthoCameraToBestAxisAlignedView !== true
                    : false;
            if (!isMouse && touchCount.current > 1) {
                hideSvgCameraCursor();
                return;
            }

            const startedMoving = isMouse || movedTouchAfterPressed.current;
            if (
                startedMoving &&
                !moveSvgCameraCursor({
                    svg,
                    view,
                    x,
                    y,
                    canvas,
                    buttons,
                    isMouse: isMouse,
                    orbitOnPrimaryButton,
                    enableOrthoOrbitCursor: orthoMouseRotateEnabled,
                    spacebarHeld: spacebarPanState.held,
                    spacebarPanning: spacebarPanState.clickOrigin !== null,
                }) &&
                canvas
            ) {
                canvas.style.cursor = "default";
            }
            isCameraCursorDrawn.current = true;
        }
    };

    const clippingPlaneCommitTimer = useRef<ReturnType<typeof setTimeout>>();
    const moveClippingPlanes = (delta: number) => {
        if (!view || cameraType === CameraType.Orthographic) {
            return;
        }

        if (!movingPlaneControl.current) {
            movingPlaneControl.current = movePlanes(
                view,
                clippingPlanes.planes,
                clippingPlanes.planes.map((_, i) => i),
            );
        }

        if (clippingPlaneCommitTimer.current) {
            clearTimeout(clippingPlaneCommitTimer.current);
            clippingPlaneCommitTimer.current = undefined;
        }

        const newValues = view.renderState.clipping.planes.map((p) => p.normalOffset[3] - delta);
        movingPlaneControl.current.update(newValues);

        clippingPlaneCommitTimer.current = setTimeout(() => {
            movingPlaneControl.current?.finish(true);
            movingPlaneControl.current = undefined;
            clippingPlaneCommitTimer.current = undefined;
        }, 100);
    };

    const turnOffLocationAutocenter = () => {
        if (selectMyLocationAutocenter(store.getState())) {
            dispatch(myLocationActions.toggleAutocenter(false));
        }
    };

    const handleDown = async (x: number, y: number, timestamp: number) => {
        pointerDownStateRef.current = {
            timestamp,
            x,
            y,
        };
        mouseButtonPressed.current = true;
    };

    const onWheel = (e: ReactWheelEvent<HTMLCanvasElement>) => {
        if (!e.shiftKey || !clippingPlanes.enabled) {
            turnOffLocationAutocenter();
            return;
        }

        moveClippingPlanes(-(e.deltaY / 100));
    };

    const onContextMenu = (evt: MouseEvent<HTMLCanvasElement>) => {
        // The desktop right-click context menu is opened from `onMouseUp` (button === 2).
        // On touch: for Procore the context menu opens on pick-selection from the
        // canvas click handler, so we only suppress the browser's default touch context
        // menu. For non-Procore (app build) we still open the menu via long-press, and
        // also via the platform's native context-menu event as a fallback.
        const isTouch = evt.nativeEvent instanceof PointerEvent && evt.nativeEvent.pointerType === "touch";
        if (!isTouch) {
            return;
        }

        evt.preventDefault();

        if (isProcoreDesign && picker === Picker.SectionPlane) {
            return;
        }

        if (!isProcoreDesign) {
            handleCanvasContextMenu([evt.nativeEvent.offsetX, evt.nativeEvent.offsetY], isTouch);
        }
    };

    const onTouchStart = (e: ReactTouchEvent<HTMLCanvasElement>) => {
        if (touchCount.current === 0 && e.touches.length > 0) {
            touchStartPos.current = vec2.fromValues(e.touches[0].clientX, e.touches[0].clientY);
        }
        touchCount.current = e.touches.length;
    };

    const prevPinchDiff = useRef<number>(0);
    const onTouchMove = (e: ReactTouchEvent<HTMLCanvasElement>) => {
        if (e.touches.length === 4 && clippingPlanes.enabled) {
            const touches = Array.from(e.touches).sort((a, b) => a.clientY - b.clientY);
            const top = (touches[0].clientY + touches[1].clientY + touches[2].clientY) / 3;
            const bot = touches[3].clientY;
            const diff = top - bot;

            if (Math.abs(prevPinchDiff.current - diff) >= 1) {
                moveClippingPlanes(Math.sign(prevPinchDiff.current - diff) * 0.1);
            }

            prevPinchDiff.current = diff;
        }
    };

    const onTouchEnd = (e: ReactTouchEvent<HTMLCanvasElement>) => {
        touchCount.current = e.touches.length;
        if (touchCount.current === 0) {
            movedTouchAfterPressed.current = false;
        }
        hideSvgCameraCursor();
    };

    const contextMenuCursorState = useRef<{
        timestamp: number;
        startPos: Vec2;
        currentPos: Vec2;
    }>();

    const onMouseDown = async (e: MouseEvent) => {
        contextMenuCursorState.current = undefined;
        if (e.button === 2) {
            contextMenuCursorState.current = {
                timestamp: e.timeStamp,
                startPos: [e.nativeEvent.offsetX, e.nativeEvent.offsetY],
                currentPos: [e.nativeEvent.offsetX, e.nativeEvent.offsetY],
            };
            return;
        } else if (e.button !== 0) {
            return;
        }

        await handleDown(e.nativeEvent.offsetX, e.nativeEvent.offsetY, e.timeStamp);
    };

    const prevHoverUpdate = useRef(0);
    const prevHoverEnt = useRef<Awaited<ReturnType<CoreModule["pickMeasureEntityOnCurrentObject"]>>>();
    const previous2dSnapPos = useRef(vec2.create());
    const lastPointerMoveEvent = useRef<PointerMoveEvent | null>(null);
    const outlineHoverGate = useRef(createLatestGate()).current;
    const onPointerMove = async (e: PointerMoveEvent) => {
        pointerPosRef.current = [e.nativeEvent.offsetX, e.nativeEvent.offsetY];
        lastPointerMoveEvent.current = {
            movementX: e.movementX,
            movementY: e.movementY,
            nativeEvent: {
                offsetX: e.nativeEvent.offsetX,
                offsetY: e.nativeEvent.offsetY,
            },
            buttons: e.buttons,
        };
        if (roadLayerTracerEnabled) {
            // TODO: dont call renderfn directly from here.
            engine2dRenderFnRef.current?.(false, false);
        }
        const hasMovementForced = e.handleEvenWhenNoMovement ? true : e.movementY || e.movementX;
        if (!view || !canvas || !svg || !hasMovementForced) {
            return;
        }

        const planePicking = cameraType === CameraType.Orthographic && view.renderState.camera.far < 1;
        // For the generic picking session, honor the consumer-provided snap tolerance; otherwise use
        // the tool default derived from the active picker.
        const pickingSession = pickingSessionRef.current;
        const effectiveHoverSettings =
            picker === Picker.SdkPicking && pickingSession ? pickingSession.tolerance : measureHoverSettings;
        const hoverOutline = async (cursorPosition: vec3, tolerance: SnapTolerance) => {
            if (view.renderState.clipping.planes.length && tolerance.point) {
                if (planePicking) {
                    return await queryView(view, "selectOutlinePoint", cursorPosition, tolerance.point);
                } else {
                    const plane = view.renderState.clipping.planes[0].normalOffset;
                    const planeDir = vec3.fromValues(plane[0], plane[1], plane[2]);
                    const rayDir = vec3.sub(vec3.create(), view.renderState.camera.position, cursorPosition);
                    vec3.normalize(rayDir, rayDir);
                    const d = vec3.dot(planeDir, rayDir);
                    if (d > 0) {
                        const t = (plane[3] - vec3.dot(planeDir, view.renderState.camera.position)) / d;
                        const pos = vec3.scaleAndAdd(vec3.create(), view.renderState.camera.position, rayDir, t);
                        return await queryView(view, "selectOutlinePoint", pos, tolerance.point);
                    }
                }
            }
        };
        const pointToHover = (point: vec3, objectId: number) => {
            return {
                status: "loaded" as LoadStatus,
                connectionPoint: point,
                entity: {
                    ObjectId: objectId,
                    drawKind: "vertex",
                    parameter: point,
                } as MeasureEntity,
            };
        };

        const getHoverEntityStrokeColor = (
            localHoverEnt: typeof prevHoverEnt.current,
            localResult: PickSampleExt | undefined,
        ) =>
            !localHoverEnt?.entity && !localResult?.objectId
                ? "red"
                : localHoverEnt?.status === "loaded"
                  ? "lightgreen"
                  : localHoverEnt?.status === "unknown"
                    ? "blue"
                    : "yellow";

        const handleHoverEntity = async ({
            view,
            result,
            updateCursorStroke,
            onLoaded,
        }: {
            view: View;
            result: PickSampleExt | undefined;
            updateCursorStroke?: boolean;
            onLoaded?: (hoverEnt: MeasureEntityPickResult | undefined) => void;
        }) => {
            let hoverEnt = prevHoverEnt.current;
            const now = performance.now();
            const shouldPickHoverEnt = now - prevHoverUpdate.current > 75 && !view.activeController.moving;
            const checkResetHover = () => {
                const currentPos = vec2.fromValues(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
                if (vec2.dist(currentPos, previous2dSnapPos.current) > 25) {
                    hoverEnt = undefined;
                }
            };

            if (shouldPickHoverEnt) {
                prevHoverUpdate.current = now;

                // Returns false when a newer hover superseded this one while its outline query was pending.
                const handleHover = async (result: PickSampleExt | undefined) => {
                    // Whatever this hover does, an older outline query still pending must not apply.
                    outlineHoverGate.cancel(outlineHoverGate.begin());
                    if (
                        picker === Picker.Measurement ||
                        picker === Picker.Area ||
                        picker === Picker.PointLine ||
                        picker === Picker.Coordinates ||
                        picker === Picker.Dimension ||
                        picker === Picker.SdkPicking
                    ) {
                        if (result) {
                            let outlinePoint: vec3 | undefined;
                            if (view.renderState.clipping.planes.length && effectiveHoverSettings.point) {
                                const tolerance = applyCameraDistanceToMeasureTolerance(
                                    result.position,
                                    view.renderState.camera,
                                    effectiveHoverSettings,
                                );
                                const outlineTicket = outlineHoverGate.begin();
                                outlinePoint = (await hoverOutline(result.position, tolerance)) as vec3 | undefined;
                                if (!outlineHoverGate.accept(outlineTicket)) {
                                    return false;
                                }
                            }
                            if (outlinePoint) {
                                hoverEnt = pointToHover(outlinePoint, result.objectId);
                            } else if (view.measure && !planePicking) {
                                const tolerance = applyCameraDistanceToMeasureTolerance(
                                    result.position,
                                    view.renderState.camera,
                                    effectiveHoverSettings,
                                );
                                hoverEnt = await view.measure.core.pickMeasureEntityOnCurrentObject(
                                    result.objectId,
                                    result.position,
                                    tolerance,
                                    allowGeneratedParametric.enabled,
                                );
                            }
                            vec2.copy(
                                previous2dSnapPos.current,
                                vec2.fromValues(e.nativeEvent.offsetX, e.nativeEvent.offsetY),
                            );
                        } else {
                            checkResetHover();
                        }
                        prevHoverEnt.current = hoverEnt;
                    } else if (picker === Picker.CrossSection) {
                        const position =
                            result?.position ??
                            view.convert.screenSpaceToWorldSpace([
                                vec2.fromValues(e.nativeEvent.offsetX, e.nativeEvent.offsetY),
                            ])[0];
                        if (crossSectionPoint && position) {
                            dispatch(orthoCamActions.setCrossSectionHover(position as vec3));
                        }
                    }
                    dispatch(
                        measureActions.selectHoverObj(
                            hoverEnt?.entity
                                ? {
                                      ...hoverEnt.entity,
                                      connectionPoint: hoverEnt.connectionPoint,
                                  }
                                : undefined,
                        ),
                    );
                    return true;
                };
                const hoverApplied = await handleHover(result);

                // Load hovered brep
                if (
                    hoverApplied &&
                    (picker === Picker.Measurement ||
                        picker === Picker.Area ||
                        picker === Picker.PointLine ||
                        picker === Picker.Coordinates ||
                        picker === Picker.Dimension ||
                        picker === Picker.SdkPicking) &&
                    hoverEnt?.status === "unknown"
                ) {
                    if (!result || result.objectId !== lastMeasurePickResult.current?.objectId) {
                        if (downloadHoveredBrepTimer.current) {
                            clearTimeout(downloadHoveredBrepTimer.current);
                            downloadHoveredBrepTimer.current = undefined;
                        }
                    }

                    if (result && result.objectId !== lastMeasurePickResult.current?.objectId) {
                        const timer = setTimeout(async () => {
                            const shouldStop = () =>
                                timer !== downloadHoveredBrepTimer.current ||
                                result.objectId !== lastMeasurePickResult.current?.objectId;

                            const tolerance = applyCameraDistanceToMeasureTolerance(
                                result.position,
                                view.renderState.camera,
                                effectiveHoverSettings,
                            );

                            // TODO consider better way to download brep
                            const hoverEnt = await view.measure?.core.pickMeasureEntity(
                                result.objectId,
                                result.position,
                                tolerance,
                                allowGeneratedParametric.enabled,
                            );

                            if (shouldStop()) {
                                return;
                            }

                            await handleHover(lastMeasurePickResult.current);

                            if (shouldStop()) {
                                return;
                            }

                            if (updateCursorStroke) {
                                svg?.querySelector("#cursor line, #cursor path")?.setAttribute(
                                    "stroke",
                                    getHoverEntityStrokeColor(hoverEnt, result),
                                );
                            }
                            onLoaded?.(prevHoverEnt.current);
                            downloadHoveredBrepTimer.current = undefined;
                        }, 500);

                        downloadHoveredBrepTimer.current = timer;
                    }
                }

                if (hoverApplied) {
                    lastMeasurePickResult.current = result;
                }
            }

            return hoverEnt;
        };

        const handlePickAndHoverEntProcessing = async () => {
            const result = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY, {
                sampleDiscRadius: 4,
                async: false,
                pickCameraPlane: planePicking,
            });

            const hoverEnt = await handleHoverEntity({ view, result });

            let cursorX = e.nativeEvent.offsetX;
            let cursorY = e.nativeEvent.offsetY;

            let pointSnapGuides: ReadonlyVec2[] | undefined;
            let snapType: "corner" | "tangent" | "vertex" | "normal" | "midpoint" = "normal";
            if (picker === Picker.PointLine || picker === Picker.Coordinates) {
                const snapInfo =
                    hoverEnt?.entity &&
                    hoverEnt.connectionPoint &&
                    (await getSnapInfo({
                        view,
                        entity: hoverEnt.entity,
                        connectionPoint: hoverEnt.connectionPoint,
                        corners: true,
                    }));
                snapType = snapInfo?.type || "normal";
                const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
                const snapInfo2d = snapInfo && getSnapInfo2d(view, toScreenSpace, snapInfo);
                if (snapInfo2d) {
                    cursorX = snapInfo2d.connection[0];
                    cursorY = snapInfo2d.connection[1];
                    pointSnapGuides = getSnapGuides2d(snapInfo2d);
                }
            }

            return {
                result,
                cursorX,
                cursorY,
                pointSnapGuides,
                snapType,
                hoverEnt,
            };
        };

        if (e.buttons !== 0) {
            turnOffLocationAutocenter();
        }

        if (isDraggingLaser || isDraggingCoordinate || isDraggingPoint || isMarkerPointerInteractionActive) {
            hideSvgCameraCursor();
        } else if ((e.buttons === 1 || e.buttons === 2 || e.buttons === 4) && !cameraDefaults.pinhole.usePointerLock) {
            doMoveSvgCameraCursor(e.nativeEvent.offsetX, e.nativeEvent.offsetY, e.buttons);
        }

        const mobileMeasurePickers = [Picker.Dimension, Picker.PointLine, Picker.OutlineLaser, Picker.Coordinates];
        if (isMobileMeasureInteraction && mobileMeasurePickers.includes(picker)) {
            return;
        }

        if (cursor === "cross" && picker === Picker.OutlineLaser && (newDesign || isProcoreDesign)) {
            if (e.buttons !== 0) {
                hideSvgCursor();
                return;
            }

            if (hideLaserGizmo) {
                hideSvgCursor();
            } else if (cameraType === CameraType.Orthographic && clippingPlanes.planes.length > 0) {
                hideSvgCursor();
            } else {
                const result = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY, {
                    sampleDiscRadius: 4,
                    async: e.forceAsyncPick ?? false,
                    pickCameraPlane: planePicking,
                });

                moveSvgCursor({
                    svg,
                    view,
                    size,
                    pickResult: result,
                    x: e.nativeEvent.offsetX,
                    y: e.nativeEvent.offsetY,
                    color: result ? "none" : "white",
                    overrideKind: "rotatedCross",
                });
            }
        } else if (
            (e.buttons === 0 || e.buttons === 1) &&
            cursor === "cross" &&
            picker === Picker.Coordinates &&
            isProcoreDesign
        ) {
            if (e.buttons !== 0 && !isDraggingCoordinate) {
                hideSvgCursor();
                return;
            }

            const { result, cursorX, cursorY, pointSnapGuides, snapType, hoverEnt } =
                await handlePickAndHoverEntProcessing();

            if (hideCoordinateGizmo) {
                hideSvgCursor();
            } else {
                const overrideKind = !result
                    ? "rotatedCross"
                    : snapType === "normal"
                      ? "coordinatesSurface"
                      : "pointSnap";
                if (overrideKind === "pointSnap") {
                    moveSvgCursorPointSnap({
                        svg,
                        view,
                        x: cursorX,
                        y: cursorY,
                        pointSnapGuides,
                        snap: false,
                    });
                } else {
                    moveSvgCursor({
                        svg,
                        view,
                        size,
                        pickResult: result,
                        x: cursorX,
                        y: cursorY,
                        color: result ? "none" : "white",
                        overrideKind,
                        pointSnapGuides,
                        canvas,
                    });
                }

                const coordinatePos = hoverEnt?.connectionPoint || result?.position || [0, 0, 0];

                const formattedX = convertAndFormat(uom, coordinatePos[0] || 0, UnitType.length, {
                    decimals: 3,
                });
                const formattedY = convertAndFormat(uom, coordinatePos[1] || 0, UnitType.length, {
                    decimals: 3,
                });
                const formattedZ = convertAndFormat(uom, coordinatePos[2] || 0, UnitType.length, {
                    decimals: 3,
                });

                updateTempCoordinateLabel(
                    {
                        x: cursorX,
                        y: cursorY,
                        position: coordinatePos,
                        normalVS: result?.normalVS,
                        screenOffset: { x: result?.x || 0, y: result?.y || 0 },
                        formattedPos: [formattedX, formattedY, formattedZ],
                        isSurface: overrideKind !== "pointSnap",
                    },
                    svg,
                );
            }
        } else if (picker === Picker.PointLine || picker === Picker.SdkPicking) {
            if (e.buttons !== 0 && !isDraggingPoint) {
                hideSvgCursor();
                return;
            }

            const result = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY, {
                sampleDiscRadius: 4,
                async: false,
                pickCameraPlane: planePicking,
            });

            const handle = async (hoverEnt: MeasureEntityPickResult | undefined) => {
                if (!result && !hoverEnt) {
                    moveSvgCursor({
                        svg,
                        view,
                        size,
                        pickResult: result,
                        x: e.nativeEvent.offsetX,
                        y: e.nativeEvent.offsetY,
                        color: result ? "none" : "white",
                        overrideKind: "rotatedCross",
                    });
                    dispatchLastPickSample(lastPickSampleActions.set(null));
                    dispatch(measureActions.selectHoverObj(undefined));
                    hideHoverCursorProgress();
                    pickingSession?.onHover?.(null);
                    return;
                }

                const { snapInfo, snapTo } = await getPointLineSnapInfo({
                    view,
                    position2d: [e.nativeEvent.offsetX, e.nativeEvent.offsetY],
                    hoverEnt,
                    pointLines,
                    currentPointLineIdx,
                    draggingPoint,
                });

                dispatch(pointLineActions.setPointLineSnapToPoint(snapTo));

                let cursorX = e.nativeEvent.offsetX;
                let cursorY = e.nativeEvent.offsetY;
                let pointSnapGuides: (ReadonlyVec2 | undefined)[] = [];
                if (snapInfo) {
                    const snapInfo2d = getSnapInfo2d(
                        view,
                        view.convert.worldSpaceToScreenSpaceConverter({ round: false }),
                        snapInfo,
                    );
                    if (snapInfo2d) {
                        cursorX = snapInfo2d.connection[0];
                        cursorY = snapInfo2d.connection[1];
                        pointSnapGuides = getSnapGuides2d(snapInfo2d);
                    }
                }

                if (!isProcoreDesign) {
                    if (hoverEnt?.status === "unknown") {
                        showHoverCursorProgress(cursorX, cursorY);
                    } else {
                        hideHoverCursorProgress();
                    }
                }

                const color =
                    !isProcoreDesign && hoverEnt?.status === "unknown" ? HOVER_CURSOR_PROGRESS_COLOR : undefined;

                moveSvgCursorPointSnap({
                    svg,
                    view,
                    x: cursorX,
                    y: cursorY,
                    pointSnapGuides,
                    snap: snapTo !== null,
                    canvas,
                    color,
                });

                dispatchLastPickSample(lastPickSampleActions.set(result ? { ...result, hoverEntity: hoverEnt } : null));

                if (pickingSession?.onHover) {
                    if (result) {
                        const adjacentVertices =
                            snapInfo && hoverEnt?.entity
                                ? await getSnapAdjacentVertices(view, hoverEnt.entity, snapInfo)
                                : undefined;
                        pickingSession.onHover({
                            position: snapInfo?.connection ?? result.position,
                            normal: result.normal,
                            snapType: (snapInfo?.type ?? "none") as PickingSnapType,
                            internalObjectId: result.objectId === -1 ? null : result.objectId,
                            adjacentVertices,
                        });
                    } else {
                        pickingSession.onHover(null);
                    }
                }
            };

            const hoverEnt = await handleHoverEntity({ view, result, onLoaded: handle });
            handle(hoverEnt);

            return;
        } else if (picker === Picker.Dimension) {
            if (e.buttons !== 0) {
                hideSvgCursor();
                return;
            }

            const result = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY, {
                sampleDiscRadius: 4,
                async: false,
                pickCameraPlane: planePicking,
            });

            const hoverEntity = await handleHoverEntity({ view, result, updateCursorStroke: true });

            const isSelected = hoverEntity?.entity
                ? measure.selectedEntities.some((entities: ExtendedMeasureEntity[]) =>
                      entities.some(
                          (selectedEntity: ExtendedMeasureEntity) =>
                              selectedEntity.ObjectId === hoverEntity.entity!.ObjectId &&
                              selectedEntity.instanceIndex === hoverEntity.entity!.instanceIndex &&
                              selectedEntity.pathIndex === hoverEntity.entity!.pathIndex &&
                              selectedEntity.drawKind === hoverEntity.entity!.drawKind,
                      ),
                  )
                : false;

            if (isProcoreDesign) {
                if (!result || hoverEntity?.entity?.drawKind === "vertex") {
                    moveSvgCursor({
                        svg,
                        view,
                        size,
                        pickResult: undefined,
                        x: e.nativeEvent.offsetX,
                        y: e.nativeEvent.offsetY,
                        color: "white",
                        overrideKind: "rotatedCross",
                    });
                } else {
                    moveSvgCursorDimension({
                        svg,
                        view,
                        x: e.nativeEvent.offsetX,
                        y: e.nativeEvent.offsetY,
                        snap: Boolean(result),
                        normalVS: result?.normalVS,
                        size,
                        isSelected,
                        canvas,
                    });
                }
            } else {
                moveSvgCursorPointSnap({
                    svg,
                    view,
                    x: e.nativeEvent.offsetX,
                    y: e.nativeEvent.offsetY,
                    pointSnapGuides: undefined,
                    snap: Boolean(result),
                    canvas,
                });
            }

            dispatchLastPickSample(lastPickSampleActions.set(result ? { ...result, hoverEntity } : null));

            return;
        } else if (e.buttons === 0 && cursor === "measure") {
            const result = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY, {
                sampleDiscRadius: 4,
                async: false,
                pickCameraPlane: planePicking,
            });

            const useSectionPlaneCursor = picker === Picker.SectionPlane;
            const clippingCursor = picker === Picker.ClippingPlane || useSectionPlaneCursor;
            const planeNormal = result ? getPlaneCreationNormal(result.normal) : undefined;
            const validForPlaneSnap = result?.sampleType === "surface" && Boolean(planeNormal);

            if (clippingCursor && !validForPlaneSnap) {
                dispatch(renderActions.setSectionPlaneHoverObjectId(null));
                moveSvgCursor({
                    svg,
                    view,
                    size,
                    pickResult: undefined,
                    x: e.nativeEvent.offsetX,
                    y: e.nativeEvent.offsetY,
                    color: "white",
                    overrideKind: "rotatedCross",
                });
                dispatchLastPickSample(lastPickSampleActions.set(result ?? null));
                return;
            }

            if (useSectionPlaneCursor && validForPlaneSnap) {
                dispatch(renderActions.setSectionPlaneHoverObjectId(result!.objectId));
            }

            // In section plane mode, skip normal entity hover.
            if (isProcoreDesign && useSectionPlaneCursor) {
                moveSvgCursor({
                    svg,
                    view,
                    size,
                    pickResult: result,
                    x: e.nativeEvent.offsetX,
                    y: e.nativeEvent.offsetY,
                    color: "",
                    overrideKind: validForPlaneSnap ? "sectionPlane" : undefined,
                });
                dispatchLastPickSample(lastPickSampleActions.set(result ?? null));
                return;
            }

            const hoverEnt = await handleHoverEntity({ view, result, updateCursorStroke: true });

            const color = getHoverEntityStrokeColor(hoverEnt, result);

            if (!hoverEnt?.entity || hoverEnt.entity.drawKind === "face") {
                moveSvgCursor({
                    svg,
                    view,
                    size,
                    pickResult: result,
                    x: e.nativeEvent.offsetX,
                    y: e.nativeEvent.offsetY,
                    color,
                    overrideKind: useSectionPlaneCursor && validForPlaneSnap ? "sectionPlane" : undefined,
                });
            } else {
                moveSvgCursor({
                    svg,
                    view,
                    size,
                    pickResult: undefined,
                    x: e.nativeEvent.offsetX,
                    y: e.nativeEvent.offsetY,
                    color: color,
                    overrideKind: hoverEnt.entity.drawKind === "edge" ? "edge" : "corner",
                });
            }

            dispatchLastPickSample(lastPickSampleActions.set(result ?? null));

            return;
        } else if (cursor === "cross") {
            moveSvgCursor({
                svg,
                view,
                size,
                pickResult: undefined,
                x: e.nativeEvent.offsetX,
                y: e.nativeEvent.offsetY,
                color: "white",
                overrideKind: "cross",
            });
        } else {
            hideSvgCursor();
        }

        const showInlineStamp =
            !stamp?.pinned &&
            cameraType === CameraType.Orthographic &&
            e.buttons === 0 &&
            subtrees.points === SubtreeStatus.Shown;
        const setDeviationStamp =
            showInlineStamp && [ViewMode.CrossSection, ViewMode.FollowPath, ViewMode.Deviations].includes(viewMode);
        const setPointVisStamp =
            showInlineStamp &&
            pointVisStamp.enabled &&
            ["classification", "intensity"].includes(defaultPointVisualization.kind);
        if (setDeviationStamp) {
            const isTouch = e.nativeEvent instanceof PointerEvent && e.nativeEvent.pointerType === "touch";
            const measurement = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY, {
                sampleDiscRadius: isTouch ? 8 : 1,
            });

            if (measurement?.pointFactor !== undefined) {
                dispatch(
                    renderActions.setStamp({
                        kind: StampKind.Deviation,
                        pinned: false,
                        mouseX: e.nativeEvent.offsetX,
                        mouseY: e.nativeEvent.offsetY,
                        data: { deviation: measurement.pointFactor },
                    }),
                );
            } else {
                dispatch(renderActions.setStamp(null));
            }
        } else if (setPointVisStamp) {
            const isTouch = e.nativeEvent instanceof PointerEvent && e.nativeEvent.pointerType === "touch";
            const measurement = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY, {
                sampleDiscRadius: isTouch ? 8 : 1,
            });

            if (measurement?.pointFactor !== undefined) {
                dispatch(
                    renderActions.setStamp({
                        kind: StampKind.Classification,
                        pinned: false,
                        mouseX: e.nativeEvent.offsetX,
                        mouseY: e.nativeEvent.offsetY,
                        data: { pointFactor: measurement.pointFactor },
                    }),
                );
            } else {
                dispatch(renderActions.setStamp(null));
            }
        } else if (stamp && !stamp.pinned) {
            dispatch(renderActions.setStamp(null));
        }

        if (e.buttons === 0) {
            // Claimed before the awaits below, in event order, so that of two moves in flight the
            // newer one wins however their picks happen to resolve.
            const hoverRequest = objectMeasureHover.begin();

            const lastPickSample = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY, { async: false });
            dispatchLastPickSample(lastPickSampleActions.set(lastPickSample ?? null));

            // Highlighting the object under the mouse is the affordance for picking it into an
            // object-to-object measurement, the same way the section tool marks the surface it
            // would cut. Mouse only - there is nothing to hover with a finger.
            //
            // Only while the pair is still being picked: a complete pair shows no tint at all (see
            // `buildObjectMeasureHoverHighlight`), so there would be nothing for the id to feed.
            // Re-checked against the store in `settle`, since this render's answer may be stale by
            // the time the pick resolves.
            if (picker === Picker.ObjectMeasurement && objectMeasureIds.length < 2 && hasMouseSupport()) {
                // Its own pick, not the sample above: that one is taken without a disc radius,
                // while the click this previews uses one (see the pick in `useCanvasClickHandler`).
                // On a thin object or near a silhouette the two resolve to different objects, which
                // would tint one and measure another. Same options as that click, so that what
                // lights up is what gets measured - 4 rather than the touch radius, since hovering
                // is mouse-only.
                const hoverPick = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY, {
                    sampleDiscRadius: 4,
                    async: false,
                    pickCameraPlane,
                });
                objectMeasureHover.settle(hoverRequest, hoverPick?.objectId);
            }
        } else if (picker === Picker.ObjectMeasurement) {
            // Dragging the camera slides the model under a stationary cursor, so whatever was under
            // it is no longer what a click would pick.
            objectMeasureHover.clear();
        }

        if (contextMenuCursorState.current) {
            contextMenuCursorState.current.currentPos[0] += e.movementX;
            contextMenuCursorState.current.currentPos[1] += e.movementY;
        }
    };
    const onPointerMoveRef = useRef(onPointerMove);
    onPointerMoveRef.current = onPointerMove;

    // Intercept pointer events during select box drag to prevent camera movement while still updating select box.
    useEffect(() => {
        if (!canvas || !selectBox) {
            return;
        }
        const stopEvent = (e: Event & { stopImmediatePropagation?: () => void }) => {
            e.preventDefault();
            if (e.stopImmediatePropagation) {
                e.stopImmediatePropagation();
            } else {
                e.stopPropagation();
            }
        };
        // Capture-phase listener & stops underlying viewer handlers.
        const onPointerMoveCapture = (ev: PointerEvent) => {
            if (selectBox) {
                const rect = canvas.getBoundingClientRect();
                dispatch(
                    renderActions.setSelectBox({
                        startX: selectBox.startX,
                        startY: selectBox.startY,
                        endX: ev.clientX - rect.left,
                        endY: ev.clientY - rect.top,
                    }),
                );
                stopEvent(ev);
            }
        };
        const onWheelCapture = (e: WheelEvent) => stopEvent(e);
        const onContextMenuCapture = (e: Event & { stopImmediatePropagation?: () => void }) => stopEvent(e);
        canvas.addEventListener("pointermove", onPointerMoveCapture, true);
        canvas.addEventListener("wheel", onWheelCapture, { passive: false, capture: true });
        canvas.addEventListener("contextmenu", onContextMenuCapture, true);

        // Also block window-level pointermove in case the flight controller listens there.
        const onWindowPointerMoveCapture = (ev: PointerEvent) => {
            if (ev.buttons & 1 || selectBox) {
                const rect = canvas.getBoundingClientRect();
                dispatch(
                    renderActions.setSelectBox({
                        startX: selectBox.startX,
                        startY: selectBox.startY,
                        endX: ev.clientX - rect.left,
                        endY: ev.clientY - rect.top,
                    }),
                );
                stopEvent(ev);
            }
        };
        globalThis.addEventListener("pointermove", onWindowPointerMoveCapture, true);

        return () => {
            canvas.removeEventListener("pointermove", onPointerMoveCapture, true);
            canvas.removeEventListener("wheel", onWheelCapture, true);
            canvas.removeEventListener("contextmenu", onContextMenuCapture, true);
            globalThis.removeEventListener("pointermove", onWindowPointerMoveCapture, true);
        };
    }, [canvas, dispatch, selectBox]);

    useEffect(() => {
        // Some tools like coordinates and point line have custom cursors which are updated inside onPointerMove.
        // When switching to these tools with a keyboard shortcut - onPointerMove is not called,
        // but default cursor for the canvas is being disabled, so we end up not seeing any cursor at all
        // until we move the mouse.
        // To fix this we call onPointerMove whenever picker changes.
        // cursor is among the deps because when picker changes - cursor is still "standard"
        const e = lastPointerMoveEvent.current;
        if (e && (picker === Picker.Coordinates || picker === Picker.PointLine || picker === Picker.Dimension)) {
            const timeout = setTimeout(
                () =>
                    onPointerMoveRef.current({
                        ...e,
                        buttons: 0,
                        forceAsyncPick: true,
                        handleEvenWhenNoMovement: true,
                    }),
                0,
            );
            return () => clearTimeout(timeout);
        }
    }, [picker, cursor]);

    const finalizeSelectBox = async (box: ReturnType<typeof selectSelectBox>) => {
        // A newer box (or a later real canvas click) bumps generation so this resolve cannot land.
        const { generation, signal } = beginSelectBoxAbort();

        // Selection can also be claimed outside the canvas (tree, Select Similar, etc.),
        // where there is no shared abort call. The selected sets are immutable, so identity
        // is a cheap ownership token even when highlighted contains millions of ids.
        const selectionAtStart = {
            highlightedIds: lazyHighlightedObjects.current.ids,
            mainObject: selectMainObject(appStore.getState()),
            mainObjects: selectMainObjects(appStore.getState()),
        };
        const selectionIsStillOwnedByBox = () =>
            lazyHighlightedObjects.current.ids === selectionAtStart.highlightedIds &&
            selectMainObject(appStore.getState()) === selectionAtStart.mainObject &&
            selectMainObjects(appStore.getState()) === selectionAtStart.mainObjects;

        // Read off the store, not the selected value: the box awaits view.selectBox before
        // it touches the selection, so a value captured on render is stale by then and an
        // isolation that turns X-ray on mid-flight would lose to the landing box.
        const xRayActive = selectIsXRayActive(appStore.getState());
        if (!box || !view || xRayActive) {
            if (box && xRayActive) {
                dispatch(renderActions.setSelectBox(null));
            }
            return;
        }

        const { startX, startY, endX, endY } = box;

        // Set the guard window BEFORE the async work so the click handler that fires
        // on the same mouseup is suppressed while resolution is in flight.
        lastSelectBoxCompletionTime.current = Date.now();

        try {
            const selectBoxObjectIds = await view.selectBox(startX, startY, endX, endY);
            if (generation !== getSelectBoxAbortGeneration()) {
                dispatch(renderActions.setSelectBox(null));
                return;
            }
            dispatch(renderActions.setSelectBox(null));
            if (!selectionIsStillOwnedByBox()) {
                return;
            }
            if (selectBoxObjectIds.size === 0) {
                return;
            }

            if (selectIsXRayActive(appStore.getState())) {
                // An isolation turned X-ray on while the box was resolving. Its selection
                // owns the highlight set now, so this box must not toggle anything into it.
                return;
            }

            // The ownership check above guarantees these are still the values from box start.
            const currentlySelectedObjectsIds = new Set(lazyHighlightedObjects.current.idArr);
            const previousMainObject = selectionAtStart.mainObject;
            const previousMainObjects = selectionAtStart.mainObjects;
            const toAdd: number[] = [];
            const toRemove: number[] = [];

            // Toggle behavior: if already selected, remove it; if not selected, add it
            // This applies to ALL selections regardless of size
            for (const id of selectBoxObjectIds) {
                if (currentlySelectedObjectsIds.has(id)) {
                    // Already selected: remove (toggle off)
                    toRemove.push(id);
                    currentlySelectedObjectsIds.delete(id);
                } else {
                    // Not selected: add (toggle on)
                    toAdd.push(id);
                    currentlySelectedObjectsIds.add(id);
                }
            }

            toRemove.sort((a, b) => a - b);
            toAdd.sort((a, b) => a - b);

            // Check if both arrays are empty - this would mean no change
            if (toAdd.length === 0 && toRemove.length === 0) {
                return;
            }

            // mainObjects follows the box's own delta, resolved to semantic objects the way a
            // single click resolves. Deriving it from the resulting highlight set instead would
            // pull in descendant geometry, since `highlighted` is a superset of the semantic
            // selection after Select Similar or a First Object click.
            let resolvedMainObjects = applySelectionDelta(previousMainObjects, toAdd, toRemove);
            if (dataAccess) {
                const loadingHandle = addLoadingHandle({ dispatch });
                try {
                    const resolved = await resolveSelectionDelta(
                        previousMainObjects,
                        { added: toAdd, removed: toRemove },
                        async (id) => {
                            const target = await resolveSelectionTarget({
                                dataAccess,
                                objectId: id,
                                resolution: selectionResolution,
                                classAware: nodeClassSelectionEnabled,
                                abortSignal: signal,
                            });
                            return target.mainObjectId;
                        },
                        { signal },
                    );
                    if (generation !== getSelectBoxAbortGeneration()) {
                        return;
                    }
                    resolvedMainObjects = resolved;
                } finally {
                    removeLoadingHandle(loadingHandle, { dispatch });
                }
            }

            // Keep the semantic primary if it is still in the resolved set. `highlighted` is
            // only boxed geometry, so the parent id is often absent from the raw paint set.
            const resolvedNewMainObject = pickKeptPrimary(resolvedMainObjects, previousMainObject);

            if (generation !== getSelectBoxAbortGeneration() || !selectionIsStillOwnedByBox()) {
                return;
            }

            // CRITICAL: Dispatch highlighted changes BEFORE setting mainObject
            // Otherwise, the properties component may react to mainObject change and clear the selection
            // before our highlighted update takes effect
            dispatchHighlighted(highlightActions.removeAndAdd({ toRemove, toAdd }));

            // Now update mainObject/mainObjects after the selection is updated
            dispatch(renderActions.setMainObject(resolvedNewMainObject));
            dispatch(renderActions.setMainObjects(resolvedMainObjects));

            // Shrink diff arrays to reduce memory footprint in undo/redo stack.
            // This is critical for large selections (millions of IDs) where storing
            // raw arrays would cause memory overflow after ~100 undo steps.
            const shrunkAddedIds = IdArrayShrinker.shrink(toAdd);
            const shrunkRemovedIds = IdArrayShrinker.shrink(toRemove);

            const redoCallback = () => {
                const removedIds = IdArrayShrinker.expand(shrunkRemovedIds);
                const addedIds = IdArrayShrinker.expand(shrunkAddedIds);
                dispatchHighlighted(highlightActions.removeAndAdd({ toRemove: removedIds, toAdd: addedIds }));
                dispatch(renderActions.setMainObject(resolvedNewMainObject));
                dispatch(renderActions.setMainObjects(resolvedMainObjects));
            };

            const undoCallback = () => {
                const addedIds = IdArrayShrinker.expand(shrunkAddedIds);
                const removedIds = IdArrayShrinker.expand(shrunkRemovedIds);
                dispatchHighlighted(highlightActions.removeAndAdd({ toRemove: addedIds, toAdd: removedIds }));
                dispatch(renderActions.setMainObject(previousMainObject));
                dispatch(renderActions.setMainObjects(previousMainObjects));
            };

            dispatch(
                undoRedoActions.push({
                    description: "Select box multiple selection",
                    undoCallback,
                    redoCallback,
                }),
            );

            // Mark when select box completed to prevent immediate background clicks from clearing selection
            lastSelectBoxCompletionTime.current = Date.now();
        } catch (error) {
            if (generation !== getSelectBoxAbortGeneration() || signal.aborted) {
                dispatch(renderActions.setSelectBox(null));
                return;
            }
            console.error("[X] Select box error:", error);
            telemetry.trackCaughtException({
                source: "render.useCanvasEventHandlers.finalizeSelectBox",
                category: "render",
                error,
                severity: "error",
            });
            // Ensure select box UI is cleared even if there's an error
            dispatch(renderActions.setSelectBox(null));
        }
    };

    const onMouseUp = async (e: MouseEvent) => {
        mouseButtonPressed.current = false;
        hideSvgCameraCursor();
        if (e.buttons === 0 && e.button === 2) {
            if (isProcoreDesign && picker === Picker.SectionPlane) {
                contextMenuCursorState.current = undefined;
                return;
            }

            const cursorState = contextMenuCursorState.current;
            if (!cursorState) {
                return;
            }

            const longPress = e.timeStamp - cursorState.timestamp >= 300;
            const drag = vec2.dist(cursorState.startPos, cursorState.currentPos) >= 5;
            contextMenuCursorState.current = undefined;
            if (longPress || drag) {
                return;
            }

            if (e.button === 2 && picker === Picker.OutlineLaser && view) {
                const pickResult = await view.pick(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
                if (lockedLaserRotation) {
                    dispatch(clippingOutlineLaserActions.setLockedRotation(null));
                } else {
                    if (pickResult) {
                        const gizmoCoords = getLaserGizmoCoords(pickResult.position, pickResult.normal, undefined);
                        const { xDir, yDir } = gizmoCoords;
                        const outlineIntersection = await queryView(view, "screenSpaceLaser", {
                            laserPosition: pickResult.position,
                            onlyOnOutlines: cameraType === CameraType.Orthographic,
                            xDir,
                            yDir,
                            zDir: pickResult.normal,
                            autoAlign: true,
                        });
                        const rotation =
                            outlineIntersection &&
                            getLaserGizmoAlignmentRotation(outlineIntersection, pickResult.position, xDir, yDir);
                        const newLockedLaserRotation = rotation
                            ? {
                                  normal: pickResult.normal,
                                  rotation,
                                  pos: pickResult.position,
                                  intersection: {
                                      up: outlineIntersection.up[0],
                                      down: outlineIntersection.down[0],
                                      right: outlineIntersection.right[0],
                                      left: outlineIntersection.left[0],
                                  },
                              }
                            : null;
                        dispatch(clippingOutlineLaserActions.setLockedRotation(newLockedLaserRotation));
                    }
                }
            } else {
                await handleCanvasContextMenu(cursorState.currentPos, false);
            }
        } else if (e.button !== 0) {
            return;
        }
    };

    const onPointerEnter = (e: ReactPointerEvent<HTMLCanvasElement>) => {
        if (e.pointerType === "mouse") {
            if (e.buttons === 0) {
                hideSvgCameraCursor();
            }
            return;
        }
        void handleDown(e.nativeEvent.offsetX, e.nativeEvent.offsetY, e.timeStamp);
    };

    const onPointerOut = () => {
        hideSvgCursor();
        lastPointerMoveEvent.current = null;
        // Unconditionally, so a pick taken on the way out cannot land after it: nothing is hovered
        // once the pointer has left the canvas.
        objectMeasureHover.clear();
    };

    const onKeyUp = (evt: React.KeyboardEvent<HTMLCanvasElement>) => {
        if (evt.key === "Escape") {
            hideSvgCursor();
        }
    };

    useEffect(() => {
        // Technically, key up/down are not canvas event listeners, but they logically affect canvas behavior
        // these events work only when canvas is focused, but that's not always the case
        function onKeyDown(evt: KeyboardEvent) {
            if (view && evt.key === "Shift" && cameraType === CameraType.Orthographic) {
                // In ortho shift moves the camera on Z axis
                view.controllers.flight.input.disableWheelOnShift = false;
            }
            if (evt.key === "Control" || evt.key === "Meta") {
                // Activate multi-select when either Ctrl or CMD key is pressed
                dispatch(renderActions.setSelectMultiple(true));
            }
        }

        function onKeyUp(evt: KeyboardEvent) {
            if (view && evt.key === "Shift") {
                view.controllers.flight.input.disableWheelOnShift = true;
            }
            if (evt.key === "Alt" || evt.key === "Control" || evt.key === "Meta") {
                dispatch(renderActions.setSelectMultiple(false));
            }
        }

        // Pointer can move and be up outside the canvas, and we need to catch it to update the camera cursor.
        function onPointerMove(e: PointerEvent) {
            if (
                mouseButtonPressed.current &&
                (e.buttons === 1 || e.buttons === 2 || e.buttons === 4) &&
                !cameraDefaults.pinhole.usePointerLock
            ) {
                doMoveSvgCameraCursor(e.offsetX, e.offsetY, e.buttons);
            }
        }

        // Catching pointer up outside the browser window works for LMB, but not for RMB in Chrome.
        // For the RMB cursor is going to be stuck until the mouse gets back into the window.
        // To handle it, we also hide the cursor in canvas pointerenter event.
        function onPointerUp(e: PointerEvent) {
            hideSvgCameraCursor();
            if (e.button === 0 && selectBox) {
                // Finalize select box when left button released anywhere
                void finalizeSelectBox(selectBox);
            }
            if (e.button === 0 && draggingPoint) {
                if (lastMeasurePickResult.current && view) {
                    const state = store.getState();
                    const pointLines = selectPointLines(state);
                    const line = pointLines[draggingPoint.pointLineIdx];
                    const prevPoint = line?.points[draggingPoint.pointIdx];
                    const pointLineId = line?.id;
                    const pointId = prevPoint?.id;

                    dispatch(renderActions.setPicker(draggingPoint.originalPicker));
                    dispatch(pointLineActions.setDraggingPoint(null));

                    (async () => {
                        await addPointToPointLine({
                            position: lastMeasurePickResult.current!.position,
                            position2d: [e.offsetX, e.offsetY],
                            objectId: lastMeasurePickResult.current!.objectId,
                            move: { lineIndex: draggingPoint.pointLineIdx, pointIndex: draggingPoint.pointIdx },
                        });

                        if (prevPoint && pointLineId && pointId) {
                            const stateAfter = store.getState();
                            const linesAfterUpdate = selectPointLines(stateAfter);
                            const lineAfterUpdate = linesAfterUpdate.find((l) => l.id === pointLineId);
                            const newPoint = lineAfterUpdate?.points.find((p) => p.id === pointId);
                            if (lineAfterUpdate && newPoint) {
                                pushMovePointUndoRedo(
                                    dispatch,
                                    store.getState,
                                    view,
                                    pointLineId,
                                    pointId,
                                    prevPoint,
                                    newPoint,
                                );
                            }
                        }
                    })();
                } else {
                    dispatch(pointLineActions.setDraggingPoint(null));
                }
            }
        }

        function onTouchMove(e: TouchEvent) {
            if (touchCount.current === 0 || movedTouchAfterPressed.current || !touchStartPos.current) {
                return;
            }

            const dist = vec2.dist(touchStartPos.current, vec2.fromValues(e.touches[0].clientX, e.touches[0].clientY));
            if (dist < 10) {
                return;
            }

            movedTouchAfterPressed.current = true;
        }

        globalThis.addEventListener("keydown", onKeyDown);
        globalThis.addEventListener("keyup", onKeyUp);
        globalThis.addEventListener("pointermove", onPointerMove);
        globalThis.addEventListener("pointerup", onPointerUp);
        globalThis.addEventListener("touchmove", onTouchMove);
        return () => {
            globalThis.removeEventListener("keydown", onKeyDown);
            globalThis.removeEventListener("keyup", onKeyUp);
            globalThis.removeEventListener("pointermove", onPointerMove);
            globalThis.removeEventListener("pointerup", onPointerUp);
            globalThis.removeEventListener("touchmove", onTouchMove);
        };
    });

    useEffect(() => {
        if (!canvas) {
            return;
        }

        const onPointerDownCapture = (e: PointerEvent) => {
            if (e.button === 0 && e.altKey && pickerRef.current === Picker.Object && !isXRayActive) {
                // ALT + LMB creates a select box (prevents camera movement)
                // BUT does NOT activate multi-select mode - only CMD/CTRL does that
                // We need to use capture phase event listener to catch this before the flight controller sees it.
                // We also need to prevent the flight controller from seeing this event otherwise it will start moving
                // the camera.
                const rect = canvas.getBoundingClientRect();
                const offsetX = e.clientX - rect.left;
                const offsetY = e.clientY - rect.top;
                dispatch(
                    renderActions.setSelectBox({
                        startX: offsetX,
                        endX: offsetX,
                        startY: offsetY,
                        endY: offsetY,
                    }),
                );
                e.preventDefault();
                e.stopPropagation();
            }
        };
        canvas.addEventListener("pointerdown", onPointerDownCapture, true);
        return () => canvas.removeEventListener("pointerdown", onPointerDownCapture, true);
    }, [canvas, dispatch, isXRayActive]);

    // Long-press context menu for touch devices on non-Procore builds.
    // Procore opens the context menu on pick-selection (see useCanvasClickHandler),
    // so the long-press flow is intentionally skipped there.
    useEffect(() => {
        if (!canvas || isProcoreDesign) {
            return;
        }

        const longPressState = {
            current: undefined as
                | {
                      startPos: [number, number];
                      currentPos: [number, number];
                      timer: ReturnType<typeof setTimeout>;
                  }
                | undefined,
        };

        const cancelLongPress = () => {
            if (longPressState.current) {
                clearTimeout(longPressState.current.timer);
                longPressState.current = undefined;
            }
        };

        const onPointerDown = (e: PointerEvent) => {
            if (e.pointerType !== "touch") {
                return;
            }

            cancelLongPress();

            if (!e.isPrimary) {
                return;
            }

            dispatch(renderActions.setStamp(null));
            const canvasRect = canvas.getBoundingClientRect();
            const pos: [number, number] = [e.clientX - canvasRect.left, e.clientY - canvasRect.top];
            longPressState.current = {
                startPos: pos,
                currentPos: [pos[0], pos[1]],
                timer: setTimeout(() => {
                    if (longPressState.current) {
                        const dist = vec2.dist(longPressState.current.startPos, longPressState.current.currentPos);
                        if (dist <= 10) {
                            handleCanvasContextMenuRef.current(longPressState.current.currentPos, true);
                        }
                        longPressState.current = undefined;
                    }
                }, 500),
            };
        };

        const onPointerMove = (e: PointerEvent) => {
            if (!longPressState.current || e.pointerType !== "touch" || !e.isPrimary) {
                return;
            }
            const canvasRect = canvas.getBoundingClientRect();
            longPressState.current.currentPos[0] = e.clientX - canvasRect.left;
            longPressState.current.currentPos[1] = e.clientY - canvasRect.top;
        };

        canvas.addEventListener("pointerdown", onPointerDown);
        canvas.addEventListener("pointermove", onPointerMove);
        canvas.addEventListener("pointerup", cancelLongPress);
        canvas.addEventListener("pointercancel", cancelLongPress);
        return () => {
            canvas.removeEventListener("pointerdown", onPointerDown);
            canvas.removeEventListener("pointermove", onPointerMove);
            canvas.removeEventListener("pointerup", cancelLongPress);
            canvas.removeEventListener("pointercancel", cancelLongPress);
        };
    }, [canvas, dispatch, isProcoreDesign]);

    return {
        onWheel,
        onContextMenu,
        onTouchStart,
        onTouchMove,
        onTouchEnd,
        onTouchCancel: onTouchEnd,
        onMouseDown,
        onMouseUp,
        onPointerEnter,
        onPointerMove,
        onPointerOut,
        onKeyUp,
        lastSelectBoxCompletionTime,
    };
}
